import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { Separator } from '@/shared/components/ui/separator'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { User, Award, ShoppingCart, FileText } from 'lucide-react'
import { useDashboardUsuario } from '../hooks/useDashboard'

interface UsuarioDetalleProps {
  grupoId: string
  userId: number
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function UsuarioDetalle({ grupoId, userId, open, onOpenChange }: UsuarioDetalleProps) {
  const { data, isLoading } = useDashboardUsuario(grupoId, userId)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <User className="h-5 w-5 text-teal-500" />
            Detalle del Usuario
          </SheetTitle>
        </SheetHeader>

        {isLoading ? (
          <div className="space-y-4 mt-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </div>
        ) : !data ? (
          <p className="text-muted-foreground mt-4">No se encontraron datos del usuario</p>
        ) : (
          <div className="space-y-4 mt-4">
            {/* Datos del usuario */}
            {data.usuario && (
              <Card className="border border-gray-300 dark:border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <User className="h-4 w-4 text-teal-500" />
                    Informacion del Usuario
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(data.usuario).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-muted-foreground">{key}:</span>{' '}
                        <span className="font-medium">{value != null ? String(value) : '-'}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Puntos */}
            <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-teal-500/15 via-teal-500/5 via-50% to-transparent dark:from-teal-800/20 dark:via-teal-900/10 dark:via-50% dark:to-transparent">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Award className="h-4 w-4 text-teal-500" />
                  Puntos
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div>
                    <div className="text-2xl font-bold">{data.puntos.acumulados}</div>
                    <div className="text-xs text-muted-foreground">Acumulados</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{data.puntos.ultima_campana}</div>
                    <div className="text-xs text-muted-foreground">Ultima Campana</div>
                  </div>
                  <div>
                    <div className="text-2xl font-bold">{data.puntos.experiencia}</div>
                    <div className="text-xs text-muted-foreground">Experiencia</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Separator />

            {/* Puntajes detalle */}
            {data.puntajes_detalle.length > 0 && (
              <Card className="border border-gray-300 dark:border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Award className="h-4 w-4 text-teal-500" />
                    Puntajes
                    <Badge variant="secondary" className="text-xs">
                      {data.puntajes_detalle.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border border-gray-300 dark:border-gray-700 overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                        <TableRow>
                          {data.puntajes_detalle[0] &&
                            Object.keys(data.puntajes_detalle[0]).slice(0, 6).map((col) => (
                              <TableHead key={col} className="whitespace-nowrap text-xs">
                                {col}
                              </TableHead>
                            ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.puntajes_detalle.map((row, idx) => (
                          <TableRow key={idx}>
                            {Object.values(row).slice(0, 6).map((val, cidx) => (
                              <TableCell key={cidx} className="text-xs whitespace-nowrap">
                                {val != null ? String(val) : '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Ventas detalle */}
            {data.ventas_detalle.length > 0 && (
              <Card className="border border-gray-300 dark:border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ShoppingCart className="h-4 w-4 text-teal-500" />
                    Ventas
                    <Badge variant="secondary" className="text-xs">
                      {data.ventas_detalle.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border border-gray-300 dark:border-gray-700 overflow-x-auto">
                    <Table>
                      <TableHeader className="bg-gray-100 dark:bg-[hsl(220,15%,16%)]">
                        <TableRow>
                          {data.ventas_detalle[0] &&
                            Object.keys(data.ventas_detalle[0]).slice(0, 6).map((col) => (
                              <TableHead key={col} className="whitespace-nowrap text-xs">
                                {col}
                              </TableHead>
                            ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {data.ventas_detalle.map((row, idx) => (
                          <TableRow key={idx}>
                            {Object.values(row).slice(0, 6).map((val, cidx) => (
                              <TableCell key={cidx} className="text-xs whitespace-nowrap">
                                {val != null ? String(val) : '-'}
                              </TableCell>
                            ))}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Registro original */}
            {data.registro_original && (
              <Card className="border border-gray-300 dark:border-gray-700">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <FileText className="h-4 w-4 text-teal-500" />
                    Registro Original
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    {Object.entries(data.registro_original).map(([key, value]) => (
                      <div key={key}>
                        <span className="text-muted-foreground">{key}:</span>{' '}
                        <span className="font-medium">{value != null ? String(value) : '-'}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}
